/**
 * Shared FAQ accordion used by both /blog/:slug (post-level FAQs) and
 * /blog/category/:slug (category-level FAQs).
 *
 * Native <details>/<summary> for built-in accessibility, keyboard
 * support, and zero JavaScript overhead. The matching FAQPage JSON-LD
 * is injected from useFaqPageJsonLd so Google can surface these as
 * rich snippets directly in SERP.
 */
import React from "react";

export default function BlogFaqSection({
    faqs,
    eyebrow = "Frequently asked questions",
    heading = "Quick answers, in plain English.",
    testid = "blog-faq-section",
}) {
    if (!faqs || !faqs.length) return null;
    return (
        <section
            data-testid={testid}
            aria-labelledby={`${testid}-heading`}
            className="mt-12 rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#0b0e13] p-6 sm:p-8"
        >
            <p className="text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                {eyebrow}
            </p>
            <h2
                id={`${testid}-heading`}
                className="mt-2 font-serif text-2xl text-brand-ivory sm:text-3xl"
            >
                {heading}
            </h2>

            <div className="mt-6 divide-y divide-[rgba(212,175,55,0.15)]">
                {faqs.map((f, i) => (
                    <details
                        key={i}
                        data-testid={`${testid}-item-${i}`}
                        className="group py-4"
                    >
                        <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-medium text-brand-ivory transition-colors hover:text-brand-gold2 focus:outline-none focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg sm:text-lg">
                            <span className="flex-1 leading-snug">
                                {f.question}
                            </span>
                            <span
                                aria-hidden="true"
                                className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-brand-gold/40 text-brand-gold2 transition-transform group-open:rotate-45"
                            >
                                +
                            </span>
                        </summary>
                        <div
                            data-testid={`${testid}-answer-${i}`}
                            className="mt-3 text-sm leading-relaxed text-brand-ivory/80 sm:text-base"
                        >
                            {f.answer}
                        </div>
                    </details>
                ))}
            </div>
        </section>
    );
}
